<template>
  <article
    class="stage-teaser"
    :class="`kik-${position}`"
    :data-ab-id="dataAbId"
  >
    <section
      class="stage-teaser__content"
      :class="`kik-${imagePosition}`"
    >
      <div class="content">
        <div class="stage-teaser__top-title">
          {{ topTitle }}
        </div>
        <component
          :is="headingTag"
          v-if="title"
          class="stage-teaser__title"
        >
          <div class="flex gap-1">
            {{ title }}
            <KikLegalInformationDrawer
              v-if="enableLegalDisclaimer && titleOrSubtitle === 'title'"
              :legal-text="legalText"
              :title="legalTitle"
            />
          </div>
        </component>
        <div class="flex gap-1">
          {{ subText }}
          <KikLegalInformationDrawer
            v-if="enableLegalDisclaimer && titleOrSubtitle === 'subtitle'"
            :legal-text="legalText"
            :title="legalTitle"
          />
        </div>
        <div>
          <template v-if="link && ctaText">
            <SharedLink
              :link="link"
            >
              {{ ctaText }}
            </SharedLink>
          </template>
        </div>
      </div>
    </section>
    <SharedLink
      :link="imgLink"
      :title="imgTitle"
      class="stage-teaser__image"
    >
      <NuxtImg
        :src="imgSrc"
        sizes="lg:100vw 48vw"
        :srcset="imgSrcSet"
        :alt="imgAlt"
        :width="imgDimensions.width"
        :height="imgDimensions.height"
        :title="imgTitle"
        :loading="imageLoadType"
        :preload="imageLoadType === 'eager'"
        :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
        class="desktop:h-[500px]"
      />
    </SharedLink>
  </article>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  image?: any;
  mobileImage?: any;
  imageTitle?: string;
  imageLink?: any[];
  alt?: string;
  topTitle?: string;
  topTitleColor?: string;
  title?: string;
  titleColor?: string;
  subText?: string;
  subTextColor?: string;
  backgroundColor?: string;
  headingTag?: string;
  position?: string;
  imagePosition?: string;
  imageLoadType?: HTMLImageElement['loading'];
  ctaLink?: any[];
  ctaTextColor?: string;
  buttonColor?: string;
  borderColor?: string;
  dataAbId?: string;
  legalText?: string;
  legalTitle?: string;
  enableLegalDisclaimer?: boolean;
  titleOrSubtitle?: string;
  intLinkParam?: string;
  aspectRatio?: string;
  mobileAspectRatio?: any;
}

const props = withDefaults(defineProps<Props>(), {
  image: () => ({}),
  mobileImage: () => ({}),
  imageTitle: '',
  imageLink: () => [],
  topTitle: '',
  topTitleColor: '',
  title: '',
  titleColor: '',
  alt: '',
  subText: '',
  subTextColor: '',
  backgroundColor: '',
  headingTag: 'h1',
  position: 'bottom-left',
  imagePosition: 'right',
  imageLoadType: 'lazy',
  ctaLink: () => [],
  ctaTextColor: '',
  buttonColor: '',
  borderColor: '',
  dataAbId: '',
  legalText: '',
  legalTitle: '',
  enableLegalDisclaimer: false,
  titleOrSubtitle: 'title',
  intLinkParam: '',
  aspectRatio: '',
  mobileAspectRatio: () => ({}),
});

const {
  image, imageLink, imageTitle, mobileImage, alt, backgroundColor, ctaLink, topTitleColor, titleColor, subTextColor, ctaTextColor, buttonColor, borderColor, aspectRatio, mobileAspectRatio,
} = toRefs(props);
const { $domain } = useNuxtApp();

const link = computed(() => (ctaLink.value?.[0] ? magnoliaGetters.getLinkAndName(ctaLink.value[0], $domain.cmsAssetDomainPath, ctaLink.value[0].switchable?.intLinkParam) : {}));
const ctaText = computed(() => (ctaLink.value?.[0] ? ctaLink.value?.[0].ctaText : ''));
const imgSrc = computed(() => magnoliaGetters.getDefaultSrcAsset(image.value, $domain.cmsAssetDomainPath));
const imgSrcSet = computed(() => magnoliaGetters.getSrcSetImagesAsString(image.value, $domain.cmsAssetDomainPath, aspectRatio.value, mobileAspectRatio.value?.[0]?.aspectRatio, mobileImage.value?.[0]?.image));
const imgLink = computed(() => (imageLink.value?.[0] ? magnoliaGetters.getLinkAndName(imageLink.value[0], $domain.cmsAssetDomainPath, imageLink.value[0].intLinkParam) : {}));
const imgTitle = computed(() => (magnoliaGetters.getTitleImage(image.value, imageTitle.value)));
const imgAlt = computed(() => (magnoliaGetters.getAltImage(image.value, alt.value)));
const imgDimensions = computed(() => (magnoliaGetters.getImageDimensions(image.value)));
</script>

<style lang="scss" scoped>
.stage-teaser {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 40px;
  height: 500px;

  @media (max-width: $tablet-max) {
    display: block;
    position: relative;
    margin-bottom: 20px;
    height: auto;
  }

  @include for-mobile {
    &.kik-bottom-left {
      display: flex;
      flex-direction: column-reverse;
      width: 100%;

      .stage-teaser__content {
        text-align: left;
        width: 100%;
      }
    }

    &.kik-bottom-right {
      display: flex;
      flex-direction: column-reverse;

      .stage-teaser__content {
        text-align: right;
      }
    }

    &.kik-up-left {
      .stage-teaser__content {
        text-align: left;
      }
    }

    &.kik-up-right {
      .stage-teaser__content {
        text-align: right;
      }
    }
  }

  &__content {
    display: flex;
    background: v-bind(backgroundColor);
    flex-direction: column;
    padding-bottom: 80px;
    justify-content: flex-end;

    &.kik-left {
      align-items: flex-end;
      text-align: right;
      grid-column: 2;
      grid-row: 1;
    }

    &.kik-right {
      grid-column: 1;
      grid-row: 1;
    }

    @media (max-width: $tablet-max) {
      padding: 30px 16px !important;
      width: 100%;
    }

    .content {
      padding: 0 40px;

      @media (max-width: $tablet-max) {
        padding: 0;
      }
    }

    :deep(a) {
      color: v-bind(ctaTextColor);
      background: v-bind(buttonColor);
      border: 1px solid v-bind(borderColor);
      border-radius: 3px;
      text-decoration: none;
      display: inline-block;
      padding: 10px 30px;
      margin-top: 25px;

      &:hover {
        cursor: pointer;
        color: var(--c-white);
      }
    }
  }

  &__image {
    overflow: hidden;

    img {
      min-height: 500px;
      width: 100%;
      object-fit: cover;

      @media (max-width: $tablet-max) {
        min-height: unset;
      }
    }
  }

  &__top-title {
    color: v-bind(topTitleColor);
    font-size: var(--font-size--sm);
    font-weight: var(--font-weight--bold);
    text-transform: uppercase;

    @media (max-width: $tablet-min) {
      display: none;
    }
  }

  &__title {
    color: v-bind(titleColor);
    font-size: var(--h2-font-size);
    line-height: 3rem;
    font-weight: var(--font-weight--bold);
    margin-bottom: 8px;

    @media (max-width: $tablet-min) {
      font-size: 1.875rem;
      line-height: 2.4rem;
    }
  }

  &__subtitle {
    color: v-bind(subTextColor);
    font-size: var(--font-size--base);
  }
}
</style>
